import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Search, Home, ChevronRight, ChevronDown, Package, FileText, Clock,
  CheckCircle2, AlertTriangle, Loader2, ArrowLeft, X, Award,
  GitBranch, BarChart3, Timer, CircleDot, FileDown, Shield
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { formatDate, formatCurrency } from '@/utils/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const statusColor = (s) => {
  if (s === 'Complete' || s === 'Completed' || s === 'Closed') return 'text-emerald-400 bg-emerald-500/15 border-emerald-500/30';
  if (s === 'In Progress' || s === 'Ready') return 'text-blue-400 bg-blue-500/15 border-blue-500/30';
  if (s === 'Not Ready') return 'text-slate-400 bg-slate-500/15 border-slate-500/30';
  return 'text-amber-400 bg-amber-500/15 border-amber-500/30';
};
const statusDot = (s) => {
  if (s === 'Complete' || s === 'Closed') return 'bg-emerald-400';
  if (s === 'In Progress') return 'bg-blue-400';
  if (s === 'Ready') return 'bg-cyan-400';
  if (s === 'Not Ready') return 'bg-slate-500';
  return 'bg-amber-400';
};

// ── Search result row ──
const WoResultRow = ({ wo, onClick }) => (
  <div onClick={() => onClick(wo.order_number)} className="flex items-center gap-4 px-4 py-3 hover:bg-cyan-500/10 cursor-pointer border-b border-slate-800/50 transition-colors" data-testid={`wo-result-${wo.order_number}`}>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-cyan-400 font-mono font-bold text-sm">{wo.order_number}</span>
        {wo.parent_order_number && <span className="text-[10px] px-1.5 py-0.5 bg-purple-500/20 text-purple-400 rounded border border-purple-500/30">SPLIT</span>}
        {wo.is_trial && <span className="text-[10px] px-1.5 py-0.5 bg-red-500/20 text-red-400 rounded border border-red-500/30">TRIAL</span>}
        <span className="text-white text-sm">{wo.part_number}</span>
        <span className="text-slate-500 text-xs truncate">{wo.part_description}</span>
      </div>
      <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
        <span>{wo.customer}</span>
        {wo.customer_order_number && <span>PO: {wo.customer_order_number}</span>}
        <span>Qty: {wo.quantity}</span>
        {wo.due_date && <span>Due: {wo.due_date}</span>}
      </div>
    </div>
    <div className="flex items-center gap-3 shrink-0">
      <div className="w-16 h-1.5 rounded-full bg-slate-700 overflow-hidden"><div className="h-full bg-cyan-500 rounded-full" style={{ width: `${wo.completion}%` }} /></div>
      <span className="text-xs text-slate-500 font-mono w-8 text-right">{wo.completion}%</span>
      <span className={`text-xs px-2 py-0.5 rounded-full border ${statusColor(wo.current_status)}`}>{wo.current_step || wo.current_status}</span>
    </div>
    <ChevronRight className="w-4 h-4 text-slate-600" />
  </div>
);

// ── Split tree node ──
const TreeNode = ({ node, depth = 0, selected, onSelect }) => {
  const [expanded, setExpanded] = useState(true);
  const has = node.children?.length > 0;
  const isSel = node.order_number === selected;
  return (
    <div className={depth > 0 ? 'ml-6 border-l-2 border-slate-700 pl-4' : ''}>
      <div className={`flex items-center gap-3 py-2 px-3 rounded-lg cursor-pointer transition-colors ${isSel ? 'bg-cyan-500/10 border border-cyan-500/30' : 'hover:bg-slate-800/60'}`}
        onClick={() => onSelect(node.order_number)} data-testid={`tree-node-${node.order_number}`}>
        {has ? (
          <button onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }} className="text-slate-500 hover:text-cyan-400">
            {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
        ) : <div className="w-4 h-4 flex items-center justify-center"><div className={`w-2 h-2 rounded-full ${statusDot(node.current_status)}`} /></div>}
        <span className="text-cyan-400 font-mono text-sm font-bold">{node.order_number}</span>
        <span className="text-white text-sm">Qty: {node.quantity}</span>
        <span className={`text-xs px-2 py-0.5 rounded-full border ${statusColor(node.current_status)}`}>{node.current_department || node.current_status}</span>
        <div className="ml-auto flex items-center gap-2">
          <div className="w-16 h-1.5 rounded-full bg-slate-700 overflow-hidden"><div className="h-full bg-cyan-500 rounded-full" style={{ width: `${node.completion}%` }} /></div>
          <span className="text-xs text-slate-500 font-mono w-8 text-right">{node.completion}%</span>
        </div>
      </div>
      {expanded && has && node.children.map(ch => <TreeNode key={ch.order_number} node={ch} depth={depth + 1} selected={selected} onSelect={onSelect} />)}
    </div>
  );
};

// ── Collapsible section ──
const Section = ({ title, icon: Icon, children, defaultOpen = true, count, testId }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-slate-900/60 border border-slate-700 rounded-lg overflow-hidden" data-testid={testId}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-5 py-3 hover:bg-slate-800/50 transition-colors">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-cyan-400" />}
          <span className="text-white font-bold uppercase text-sm tracking-wide">{title}</span>
          {count != null && <span className="text-xs bg-cyan-500/20 text-cyan-400 px-2 py-0.5 rounded-full ml-2">{count}</span>}
        </div>
        <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${open ? '' : '-rotate-90'}`} />
      </button>
      {open && <div className="px-5 pb-5 pt-2">{children}</div>}
    </div>
  );
};

// ═══════ MAIN PAGE ═══════
const CustomerOrderHistory = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [woData, setWoData] = useState(null);
  const [woLoading, setWoLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  const loadWo = useCallback(async (orderNumber) => {
    setWoLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/customer-orders/wo/${encodeURIComponent(orderNumber)}`);
      setWoData(res.data);
      setSearchResults(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load work order');
    } finally { setWoLoading(false); }
  }, []);

  useEffect(() => {
    const wo = searchParams.get('wo');
    if (wo) loadWo(wo);
  }, [searchParams, loadWo]);

  // Debounced search
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) { setSearchResults(null); return; }
    const t = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await axios.get(`${BACKEND_URL}/api/customer-orders/search`, { params: { q: searchQuery } });
        setSearchResults(res.data);
      } catch { setSearchResults([]); }
      finally { setSearchLoading(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const handleBack = () => { setWoData(null); setSearchQuery(''); };

  const handleExportPdf = async () => {
    if (!woData) return;
    setPdfLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/customer-orders/wo/${encodeURIComponent(woData.order.order_number)}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `WO_${woData.order.order_number}_History.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF exported');
    } catch { toast.error('Failed to export PDF'); }
    finally { setPdfLoading(false); }
  };

  const o = woData?.order;
  const part = woData?.part;

  return (
    <div className="min-h-screen bg-[#050505] relative">
      {/* Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <svg className="absolute inset-0 w-full h-full opacity-30" xmlns="http://www.w3.org/2000/svg">
          <defs><pattern id="hc-co" x="0" y="0" width="56" height="100" patternUnits="userSpaceOnUse" patternTransform="scale(1.5)">
            <path d="M28 0 L56 16.67 L56 50 L28 66.67 L0 50 L0 16.67 Z M28 100 L56 83.33 L56 116.67 L28 133.33 L0 116.67 L0 83.33 Z" fill="none" stroke="rgba(6,182,212,0.5)" strokeWidth="1" />
          </pattern></defs><rect width="100%" height="100%" fill="url(#hc-co)" />
        </svg>
      </div>

      {/* Header */}
      <div className="relative z-10 bg-slate-950/80 backdrop-blur border-b border-slate-800 px-6 py-3 flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="text-slate-400 hover:text-white" data-testid="home-btn"><Home className="w-4 h-4 mr-1" /> Home</Button>
        <div className="h-5 w-px bg-slate-700" />
        <FileText className="w-5 h-5 text-cyan-400" />
        <h1 className="text-white font-bold uppercase tracking-wide text-lg">Order History</h1>
        {woData && <><div className="h-5 w-px bg-slate-700" /><span className="text-cyan-400 font-mono font-bold">{o?.order_number}</span></>}
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* ── SEARCH VIEW ── */}
        {!woData && (
          <div className="max-w-3xl mx-auto pt-12 space-y-4">
            <div className="text-center space-y-2">
              <h2 className="text-3xl md:text-4xl font-black text-white uppercase tracking-tight">Order History Lookup</h2>
              <p className="text-slate-400 text-sm">Search by Works Order, Customer PO, Customer Name, Part Number, or Package ID</p>
            </div>
            <div className="relative" data-testid="universal-search">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
              <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search work order, customer PO, part number..."
                className="pl-12 pr-10 h-14 bg-slate-900/80 border-cyan-500/30 text-white text-lg rounded-xl focus:border-cyan-400"
                autoFocus data-testid="search-input" />
              {searchQuery && <button onClick={() => { setSearchQuery(''); setSearchResults(null); }} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"><X className="w-4 h-4" /></button>}
            </div>

            {/* Search results — individual WO list */}
            {searchLoading && <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin text-cyan-400 mx-auto" /><p className="text-slate-500 text-sm mt-2">Searching...</p></div>}
            {searchResults && !searchLoading && (
              <div className="bg-slate-900 border border-cyan-500/30 rounded-lg overflow-hidden" data-testid="search-results">
                <div className="px-4 py-2 border-b border-slate-800 text-xs text-slate-500">{searchResults.length} work order{searchResults.length !== 1 ? 's' : ''} found</div>
                {searchResults.length === 0 && <div className="p-6 text-center text-slate-500">No work orders found</div>}
                <div className="max-h-[400px] overflow-y-auto">
                  {searchResults.map(wo => <WoResultRow key={wo.order_number} wo={wo} onClick={loadWo} />)}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Loading */}
        {woLoading && <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-cyan-400" /><span className="text-slate-400 ml-3">Loading work order...</span></div>}

        {/* ── WO DETAIL VIEW ── */}
        {woData && !woLoading && (
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <Button variant="ghost" size="sm" onClick={handleBack} className="text-slate-400 hover:text-white" data-testid="back-btn"><ArrowLeft className="w-4 h-4 mr-1" /> Back to Search</Button>
              <Button size="sm" onClick={handleExportPdf} disabled={pdfLoading} className="bg-red-600 hover:bg-red-500 text-white" data-testid="export-pdf-btn">
                {pdfLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileDown className="w-4 h-4 mr-1" />}
                {pdfLoading ? 'Generating...' : 'Export PDF'}
              </Button>
            </div>

            {/* Smart summary */}
            {woData.smart_summary && <div className="bg-cyan-950/30 border border-cyan-500/20 rounded-lg p-4" data-testid="smart-summary"><p className="text-cyan-100 text-sm leading-relaxed">{woData.smart_summary}</p></div>}

            {/* KPI cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="kpi-cards">
              <Kpi label="Customer" value={woData.customer} />
              <Kpi label="Customer PO" value={woData.customer_order_number} mono />
              <Kpi label="Part Number" value={part?.part_number} mono />
              <Kpi label="Quantity" value={o?.quantity} />
              <Kpi label="Delivered" value={woData.delivered} color="emerald" />
              <Kpi label="Outstanding" value={woData.outstanding} color="amber" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Kpi label="Status" value={o?.current_status} color={o?.current_status === 'Closed' ? 'emerald' : 'blue'} />
              <Kpi label="Completion" value={`${o?.completion || 0}%`} color="purple" />
              <Kpi label="Due Date" value={o?.due_date || '-'} />
              <Kpi label="Created" value={o?.created_at ? formatDate(o.created_at) : '-'} />
            </div>

            {/* Split tree */}
            {woData.split_tree?.length > 0 && (
              <Section title="Work Order Tree" icon={GitBranch} count={woData.family?.length} testId="section-split-tree">
                {woData.split_tree.map(root => <TreeNode key={root.order_number} node={root} selected={o?.order_number} onSelect={loadWo} />)}
              </Section>
            )}

            {/* Production timeline */}
            {woData.timeline?.length > 0 && (
              <Section title="Production Timeline" icon={Clock} count={woData.timeline.length} testId="section-timeline">
                <div className="max-h-[400px] overflow-y-auto pr-2">
                  {woData.timeline.map((ev, i) => (
                    <div key={i} className="flex gap-4" data-testid={`timeline-${i}`}>
                      <div className="flex flex-col items-center">
                        <div className={`w-3 h-3 rounded-full border-2 ${ev.status === 'Complete' ? 'bg-emerald-400 border-emerald-400' : ev.status === 'In Progress' ? 'bg-blue-400 border-blue-400 animate-pulse' : 'bg-slate-700 border-slate-600'}`} />
                        {i < woData.timeline.length - 1 && <div className="w-0.5 flex-1 bg-slate-700 mt-1" />}
                      </div>
                      <div className="pb-5 flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-white text-sm font-medium">{ev.event}</span>
                          <span className={`text-xs px-1.5 py-0.5 rounded border ${statusColor(ev.status)}`}>{ev.status}</span>
                        </div>
                        {ev.date && <p className="text-xs text-slate-500 mt-0.5">{formatDate(ev.date)}</p>}
                        <div className="flex gap-3 mt-1 text-xs text-slate-400 flex-wrap">
                          {ev.operator && <span>Operator: {ev.operator}</span>}
                          {ev.machine && <span>Machine: {ev.machine}</span>}
                        </div>
                        {ev.details && <p className="text-xs text-slate-500 mt-1 truncate">{ev.details}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* Process steps detail */}
            <Section title="Process Steps" icon={BarChart3} testId="section-process">
              <div className="space-y-1.5">
                {(o?.process_sequence || []).map((step, i) => {
                  const st = (o?.process_statuses || {})[String(i)] || 'Not Ready';
                  const sd = (o?.process_data || {})[String(i)] || {};
                  return (
                    <div key={i} className="flex items-center gap-3 bg-slate-800/40 rounded-lg px-3 py-2">
                      <div className={`w-2.5 h-2.5 rounded-full ${statusDot(st)}`} />
                      <span className="text-white text-sm flex-1">{step}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${statusColor(st)}`}>{st}</span>
                      {sd && typeof sd === 'object' && Object.keys(sd).length > 0 && (
                        <span className="text-[10px] text-slate-500 truncate max-w-[200px]">{Object.entries(sd).slice(0, 2).map(([k, v]) => `${k}: ${v}`).join(', ')}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </Section>

            {/* Packages */}
            <Section title="Delivery / Packaging" icon={Package} count={Object.values(woData.packages || {}).flat().length} defaultOpen={false} testId="section-packages">
              {Object.keys(woData.packages || {}).length === 0 ? <p className="text-slate-500 text-sm text-center py-3">No packages recorded</p> :
                Object.entries(woData.packages).map(([on, pkgs]) => (
                  <div key={on} className="mb-2">
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">{on}</p>
                    {pkgs.map((p, i) => (
                      <div key={i} className="flex items-center justify-between bg-slate-800/50 rounded-lg px-4 py-2 border border-slate-700/50 mb-1">
                        <div className="flex items-center gap-3"><Package className="w-4 h-4 text-amber-400" /><span className="text-amber-400 font-mono text-sm">{p.package_id}</span></div>
                        <div className="flex items-center gap-4 text-xs text-slate-400">
                          <span>Qty: <strong className="text-white">{p.quantity}</strong></span>
                          <span>{p.package_type}</span>
                          {p.created_at && <span>{formatDate(p.created_at)}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                ))
              }
            </Section>

            {/* Certificates */}
            <Section title="Certificates & Quality Records" icon={Award} count={woData.certificates?.length} defaultOpen={false} testId="section-certificates">
              {(!woData.certificates || woData.certificates.length === 0) ? <p className="text-slate-500 text-sm text-center py-3">No certificates linked</p> :
                woData.certificates.map((c, i) => (
                  <div key={i} className="flex items-center justify-between bg-slate-800/50 rounded-lg px-4 py-3 border border-slate-700/50 mb-1">
                    <div><span className="text-cyan-400 font-mono text-sm">{c.certificate_id || c.certificate_number}</span><span className="text-white text-sm ml-3">{c.certificate_type || c.type}</span></div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-slate-400">{c.status}</span>
                      {c.certificate_id && <a href={`${BACKEND_URL}/api/certificates/${c.certificate_id}/download`} target="_blank" rel="noreferrer" className="text-xs text-cyan-400 hover:text-cyan-300 underline">Download</a>}
                    </div>
                  </div>
                ))
              }
            </Section>

            {/* NCRs */}
            {woData.ncrs?.length > 0 && (
              <Section title="Non-Conformance Reports" icon={Shield} count={woData.ncrs.length} defaultOpen={false} testId="section-ncrs">
                {woData.ncrs.map((n, i) => (
                  <div key={i} className="flex items-center justify-between bg-slate-800/50 rounded-lg px-4 py-3 border border-slate-700/50 mb-1">
                    <div className="flex items-center gap-3">
                      <span className="text-red-400 font-mono text-sm">{n.ncr_number}</span>
                      <span className="text-xs px-1.5 py-0.5 bg-red-500/15 text-red-400 rounded border border-red-500/30">{n.severity}</span>
                      <span className="text-white text-sm truncate max-w-[300px]">{n.description}</span>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${statusColor(n.status === 'Closed' ? 'Closed' : 'In Progress')}`}>{n.status}</span>
                  </div>
                ))}
              </Section>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const colorMap = { cyan: 'text-cyan-400', emerald: 'text-emerald-400', amber: 'text-amber-400', blue: 'text-blue-400', purple: 'text-purple-400', white: 'text-white' };
const Kpi = ({ label, value, color = 'cyan', mono }) => (
  <div className="bg-slate-900/70 border border-slate-700/50 rounded-lg p-3 backdrop-blur">
    <p className="text-[10px] text-slate-500 uppercase tracking-wider">{label}</p>
    <p className={`text-sm font-bold mt-0.5 ${mono ? 'font-mono' : ''} ${colorMap[color] || 'text-cyan-400'} truncate`}>{value ?? '-'}</p>
  </div>
);

export default CustomerOrderHistory;
